import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Col,
  Input,
  Label,
  Row,
  Spinner,
  FormGroup,
} from 'reactstrap';
import { Btn } from '../../../AbstractElements';
import { get, postJson, putJson, del } from '../../../hook/request';
import { toast } from 'react-toastify';


type ReferenteRow = {
  ID: number;
  typeID: number;       
  nominativo: string;
  email: string;
  telefono: string;
  notifica: boolean;
};

type CategoryApi = {
  ID: number;
  meta?: unknown;
  codice?: string;
  ordine?: number;
  abilitato?: 0 | 1;
};

type CategoryOption = { id: number; label: string };



function parseMetaName(meta: unknown, lang = 'it'): string {
  if (!meta) return '';
  try {
    const obj = typeof meta === 'string' ? JSON.parse(meta) : (meta as any);
    const byLang = obj?.[lang] || {};
    return (byLang.nome ?? '').toString();
  } catch {
    return '';
  }
}

function toBoolNotifica(v: any): boolean {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v === 1;
  if (typeof v === 'string') return v === '1' || v.toLowerCase() === 'true';
  return false;
}


type Props = {clienteID: string};

const  ReferentiAccordion: React.FC <Props> = ({clienteID}) => {
  const [open, setOpen] = useState<'referenti' | ''>('referenti');
  const toggle = (id: 'referenti') => setOpen(prev => (prev === id ? '' : id));

  const [loading, setLoading] = useState(true);
  const [savingRow, setSavingRow] = useState<number | null>(null);
  const [deletingRow, setDeletingRow] = useState<number | null>(null);

  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [rows, setRows] = useState<ReferenteRow[]>([]);

  const [newCategory, setNewCategory] = useState<string>('');
  const [newNominativo, setNewNominativo] = useState<string>('');
  const [newEmail, setNewEmail] = useState<string>('');
  const [newTelefono, setNewTelefono] = useState<string>('');
  const [newNotifica, setNewNotifica] = useState<boolean>(false);

  const categoryMap = useMemo(
    () => new Map(categories.map(c => [c.id, c.label])),
    [categories]
  );

  
  useEffect(() => {
    (async () => {
      try {
        const catRes = await get<CategoryApi[] | { data?: CategoryApi[] }>(
          `/categories/parent?ID=677`
        );
        const catRoot: any = (catRes as any)?.data ?? catRes;
        const catArr: CategoryApi[] = Array.isArray(catRoot)
          ? catRoot
          : Array.isArray(catRoot?.data)
          ? catRoot.data
          : [];
        const catOpts: CategoryOption[] = catArr
          .filter(c => (c.abilitato ?? 1) === 1)
          .sort((a, b) => (a.ordine ?? 0) - (b.ordine ?? 0))
          .map(c => ({
            id: Number(c.ID),
            label: parseMetaName(c.meta, 'it') || c.codice || String(c.ID),
          }));
        setCategories(catOpts);

        const res = await get<ReferenteRow[] | { data?: ReferenteRow[] }>(
          `/clienti/referenti?ID=${clienteID}`
        );
        const root: any = (res as any)?.data ?? res;
        const arr: any[] = Array.isArray(root)
          ? root
          : Array.isArray(root?.data)
          ? root.data
          : [];

        setRows(
          arr.map(r => ({
            ID: Number(r.ID),
            typeID: Number(r.typeID ?? r.categoryID ?? r.tipoID ?? 0),
            nominativo: r.nominativo ?? '',
            email: r.email ?? '',
            telefono: r.telefono ?? '',
            notifica: toBoolNotifica(r.notifica),
          }))
        );
      } catch (e: any) {
        console.error(e);
        toast.error(e?.message || 'Errore nel caricamento referenti');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const resetNew = () => {
    setNewCategory('');
    setNewNominativo('');
    setNewEmail('');
    setNewTelefono('');
    setNewNotifica(false);
  };

  const reloadRows = async () => {
    const list = await get<ReferenteRow[] | { data?: ReferenteRow[] }>(
      `/clienti/referenti?ID=${clienteID}`
    );
    const root: any = (list as any)?.data ?? list;
    const arr: any[] = Array.isArray(root)
      ? root
      : Array.isArray(root?.data)
      ? root.data
      : [];
    setRows(
      arr.map(r => ({
        ID: Number(r.ID),
        typeID: Number(r.typeID ?? r.categoryID ?? r.tipoID ?? 0),
        nominativo: r.nominativo ?? '',
        email: r.email ?? '',
        telefono: r.telefono ?? '',
        notifica: toBoolNotifica(r.notifica),
      }))
    );
  };

  const handleAdd = async () => {
    if (!newCategory || !newNominativo || !newEmail) {
      toast.warn('Seleziona una categoria e inserisci nominativo ed email');
      return;
    }
    const payload = {
      clienteID: clienteID,
      categoryID: Number(newCategory),        
      nominativo: newNominativo.trim(),
      email: newEmail.trim(),
      telefono: newTelefono.trim() || null,
      notifica: newNotifica ? 1 : 0,          
      user_email: localStorage.getItem('email') || 'supporto@i-factory.biz',
    };
    try {
      setSavingRow(-1);
      await postJson(`/clienti/referenti`, payload);
      toast.success('Referente aggiunto');
      await reloadRows();
      resetNew();
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ?? e?.message ?? 'Errore aggiunta referente'
      );
    } finally {
      setSavingRow(null);
    }
  };

  const handleUpdate = async (row: ReferenteRow) => {
    const payload = {
      id: row.ID,
      clienteID: clienteID,
      categoryID: row.typeID,               
      nominativo: row.nominativo.trim(),
      email: row.email.trim(),
      telefono: row.telefono.trim() || null,
      notifica: row.notifica ? 1 : 0,
      user_email: localStorage.getItem('email') || 'supporto@i-factory.biz',
    };
    try {
      setSavingRow(row.ID);
      await putJson(`/clienti/referenti`, payload);
      toast.success('Referente aggiornato');
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ??
          e?.message ??
          'Errore aggiornamento referente'
      );
    } finally {
      setSavingRow(null);
    }
  };

  const handleDelete = async (row: ReferenteRow) => {
    if (!window.confirm('Eliminare questo referente?')) return;

    try {
      setDeletingRow(row.ID);
      await del(`/clienti/referenti?ID=${encodeURIComponent(String(row.ID))}`);
      setRows(prev => prev.filter(r => r.ID !== row.ID));
      toast.success('Referente eliminato');
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ??
          e?.message ??
          'Errore eliminazione referente'
      );
    } finally {
      setDeletingRow(null);
    }
  };

  if (loading) {
    return (
      <CardLike>
        <div className="d-flex justify-content-center py-5">
          <Spinner />
        </div>
      </CardLike>
    );
  }

  return (
    <Accordion
      open={open}
      toggle={() => toggle('referenti')}
      className="icons-accordion w-100"
    >
      <AccordionItem>
        <AccordionHeader
          targetId={'referenti'}
          className="gap-2 text-secondary bg-light-secondary"
        >
          <span className="font-secondary">Referenti</span>
          {open === 'referenti' ? (
            <i className="iconly-Arrow-Up-2 icli ms-auto icon font-secondary" />
          ) : (
            <i className="iconly-Arrow-Down-2 icli ms-auto icon font-secondary" />
          )}
        </AccordionHeader>
        <AccordionBody accordionId={'referenti'}>
          {/* Riga di inserimento */}
          <Row className="g-3 align-items-end mb-3">
            <Col md={2}>
              <Label>Categoria</Label>
              <Input
                type="select"
                value={newCategory}
                onChange={e => setNewCategory(e.target.value)}
              >
                <option value="">Seleziona…</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={3}>
              <Label>Nominativo</Label>
              <Input
                type="text"
                placeholder="Nome e cognome"
                value={newNominativo}
                onChange={e => setNewNominativo(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="es. nome@azienda.it"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Label>Telefono</Label>
              <Input
                type="text"
                placeholder="Telefono"
                value={newTelefono}
                onChange={e => setNewTelefono(e.target.value)}
              />
            </Col>
            <Col md={1}>
              <FormGroup className="checkbox-radio" check inline>
                <Input
                  id="inline-new-notifica-ref"
                  type="checkbox"
                  name="notifica"
                  checked={newNotifica}
                  onChange={e => setNewNotifica(e.target.checked)}
                />
                <Label htmlFor="inline-new-notifica-ref" check>
                  Notifica
                </Label>
              </FormGroup>
            </Col>

            <Col md={2} className="d-flex justify-content-end mt-2">
              <Btn
                color="success"
                className="text-white"
                onClick={handleAdd}
                disabled={savingRow === -1}
              >
                Aggiungi referente
              </Btn>
            </Col>
          </Row>

          {/* Header lista */}
          <Row className="g-3 mt-2 mb-1">
            <Col md={3}>
              <strong>Categoria</strong>
            </Col>
            <Col md={3}>
              <strong>Nominativo</strong>
            </Col>
            <Col md={2}>
              <strong>Email</strong>
            </Col>
            <Col md={2}>
              <strong>Telefono</strong>
            </Col>
            <Col md={1} className="d-flex justify-content-center">
              <strong>Notifica</strong>
            </Col>
            <Col md={1} className="text-end">
              <strong>Azioni</strong>
            </Col>
          </Row>
          <hr className="mt-0" />

          {rows.map(row => (
            <Row key={row.ID} className="g-3 align-items-center mb-3">
              <Col md={3}>
                <Input
                  type="select"
                  value={row.typeID}
                  onChange={e =>
                    setRows(prev =>
                      prev.map(r =>
                        r.ID === row.ID
                          ? { ...r, typeID: Number(e.target.value) }
                          : r
                      )
                    )
                  }
                >
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md={3}>
                <Input
                  type="text"
                  value={row.nominativo}
                  onChange={e =>
                    setRows(prev =>
                      prev.map(r =>
                        r.ID === row.ID
                          ? { ...r, nominativo: e.target.value }
                          : r
                      )
                    )
                  }
                />
              </Col>
              <Col md={2}>
                <Input
                  type="email"
                  value={row.email}
                  onChange={e =>
                    setRows(prev =>
                      prev.map(r =>
                        r.ID === row.ID
                          ? { ...r, email: e.target.value }
                          : r
                      )
                    )
                  }
                />
              </Col>
              <Col md={2}>
                <Input
                  type="text"
                  value={row.telefono}
                  onChange={e =>
                    setRows(prev =>
                      prev.map(r =>
                        r.ID === row.ID
                          ? { ...r, telefono: e.target.value }
                          : r
                      )
                    )
                  }
                />
              </Col>
              <Col md={1}>
                <FormGroup
                  className="checkbox-radio d-flex justify-content-center"
                  check
                  inline
                >
                  <Input
                    type="checkbox"
                    id={`row-notifica-ref-${row.ID}`}
                    checked={row.notifica}
                    onChange={e =>
                      setRows(prev =>
                        prev.map(r =>
                          r.ID === row.ID
                            ? { ...r, notifica: e.target.checked }
                            : r
                        )
                      )
                    }
                  />
                  <Label htmlFor={`row-notifica-ref-${row.ID}`} check>
                    {' '}
                  </Label>
                </FormGroup>
              </Col>
              <Col md={1} className="d-flex justify-content-end gap-2">
                <Btn
                  color="info"
                  className="text-white"
                  onClick={() => handleUpdate(row)}
                  disabled={savingRow === row.ID}
                >
                  Aggiorna
                </Btn>
                <Btn
                  color="danger"
                  className="text-white"
                  onClick={() => handleDelete(row)}
                  disabled={deletingRow === row.ID}
                >
                  Elimina
                </Btn>
              </Col>
            </Row>
          ))}
        </AccordionBody>
      </AccordionItem>
    </Accordion>
  );
}

function CardLike({ children }: { children: React.ReactNode }) {
  return (
    <div className="card">
      <div className="card-body">{children}</div>
    </div>
  );
}


export default  ReferentiAccordion;